/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useState, useContext, useRef, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { CMSModal } from "@/context";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { AnimatePresence, motion } from "framer-motion";

interface Column<T> {
  id: string;
  header: string;
  accessor: keyof T;
  render?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
}

interface TableProps<T> {
  data: T[] | any;
  columns: Column<T>[];
  actions?: (row: T, index: number) => React.ReactNode;
  loading?: boolean;
  selectable?: {
    active: boolean;
    selectedRows: T[];
    setSelectedRows: React.Dispatch<React.SetStateAction<T[]>>;
    rowIdKey: any; // ✅ this allows using "_id", "id", etc. safely
  };
  onFilterChange?: (filters: Record<string, string>) => void;
  setSelectedRow?: (row: any) => void;
  selectedRow?: any;
  emptyObject?: {
    icon?: any;
    title?: string;
    description?: string;
  };
  setSorted?: (value: any) => void;
}

const Table = <T,>({
  data,
  columns,
  actions,
  selectable,
  setSelectedRow,
  selectedRow,
  emptyObject,
  loading,
  setSorted,
}: TableProps<T>) => {
  const { theme } = useContext(CMSModal);

  const [sortColumnId, setSortColumnId] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Hold the last non-empty data so rows stay visible (dimmed) while
  // a search is in-flight rather than disappearing to a spinner.
  const prevDataRef = useRef<T[]>([]);
  useEffect(() => {
    if (!loading && data && data.length > 0) {
      prevDataRef.current = data;
    }
  }, [loading, data]);

  // When loading AND we already have rows from a previous fetch → keep
  // those rows visible at reduced opacity so the UI feels seamless.
  const isRefetching = !!loading && prevDataRef.current.length > 0;
  const displayData: T[] = isRefetching ? prevDataRef.current : data ?? [];

  const handleSort = (columnId: string, direction: "asc" | "desc") => {
    setSortColumnId(columnId);
    setSortDirection(direction);
    setSorted && setSorted({ columnId, direction });
  };

  const allSelected =
    selectable?.selectedRows.length === data?.length && data?.length > 0;

  const someSelected =
    selectable?.selectedRows.length! > 0 &&
    selectable?.selectedRows.length! < data?.length;

  const colSpan =
    columns.length + (actions ? 1 : 0) + (selectable?.active ? 1 : 0);

  return (
    <div
      className={twMerge(
        "w-full overflow-auto border h-[55vh] mt-14 mb-8 lg:mt-0 transition-colors",
        theme === "dark" ? "border-gray-700" : "border-gray-300",
      )}
    >
      <table
        className={twMerge(
          "min-w-[700px] w-full text-sm font-normal transition-colors",
          theme === "dark" ? "bg-[#262727] text-gray-100" : "bg-white ",
        )}
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <thead
          className={twMerge(
            "sticky top-0 z-10 h-[50px] border-b transition-colors",
            theme === "dark"
              ? "bg-[#1f1f1f] border-gray-700"
              : "bg-[#f8f9fb] border-gray-300",
          )}
        >
          <tr>
            {selectable?.active && (
              <th className="px-4 py-1 pl-4 text-left ">
                <input
                  type="checkbox"
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  className="mt-1"
                  checked={allSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      selectable?.setSelectedRows(displayData);
                    } else {
                      selectable?.setSelectedRows([]);
                    }
                  }}
                />
              </th>
            )}
            {actions && (
              <th className="px-4 py-3 text-left whitespace-nowrap">Actions</th>
            )}
            {columns.map((col) => {
              const isSorted = sortColumnId === col.id;
              return (
                <th
                  key={col.id}
                  className="px-4 py-1 text-left border-b border-gray-300 whitespace-nowrap select-none text-gray-600 font-medium"
                >
                  <div className="flex items-center">
                    {col.header}
                    {col.sortable && (
                      <div className="pl-1">
                        <span
                          className={
                            isSorted && sortDirection === "asc"
                              ? "text-gray-500 cursor-pointer"
                              : "text-gray-300 cursor-pointer"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSort(col.id, "asc");
                          }}
                        >
                          <svg
                            stroke="currentColor"
                            fill="currentColor"
                            strokeWidth="0"
                            viewBox="0 0 512 512"
                            className="-mb-1 "
                            height="1em"
                            width="1em"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M414 321.94 274.22 158.82a24 24 0 0 0-36.44 0L98 321.94c-13.34 15.57-2.28 39.62 18.22 39.62h279.6c20.5 0 31.56-24.05 18.18-39.62z"></path>
                          </svg>
                        </span>
                        <span
                          className={
                            isSorted && sortDirection === "desc"
                              ? "text-gray-500 cursor-pointer"
                              : "text-gray-300 cursor-pointer"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSort(col.id, "desc");
                          }}
                        >
                          <svg
                            stroke="currentColor"
                            fill="currentColor"
                            strokeWidth="0"
                            viewBox="0 0 512 512"
                            height="1em"
                            width="1em"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="m98 190.06 139.78 163.12a24 24 0 0 0 36.44 0L414 190.06c13.34-15.57 2.28-39.62-18.22-39.62h-279.6c-20.5 0-31.56 24.05-18.18 39.62z"></path>
                          </svg>
                        </span>
                      </div>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        {/* ── Body ───────────────────────────────────────────────── */}
        <tbody
          className={twMerge(
            "text-gray-500 transition-opacity duration-300",
            isRefetching ? "opacity-40" : "opacity-100",
          )}
        >
          {/* Initial loading (no previous data yet) */}
          {loading && displayData.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="h-[50vh]">
                <div className="flex items-center justify-center h-full">
                  <Spin indicator={<LoadingOutlined spin />} size="large" />
                </div>
              </td>
            </tr>
          ) : !loading && displayData.length === 0 ? (
            /* Empty state */
            <tr>
              <td
                colSpan={colSpan}
                className="p-10 text-center italic h-[50vh] align-middle"
              >
                <div className="flex flex-col items-center justify-center">
                  <div
                    className={
                      theme === "dark" ? "text-gray-500" : "text-gray-400"
                    }
                  >
                    {emptyObject?.icon}
                  </div>
                  <h1
                    className={twMerge(
                      "font-semibold",
                      theme === "dark" ? "text-gray-300" : "",
                    )}
                  >
                    {emptyObject?.title}
                  </h1>
                  <p
                    className={twMerge(
                      "text-xs",
                      theme === "dark" ? "text-gray-500" : "text-gray-500",
                    )}
                  >
                    {emptyObject?.description}
                  </p>
                </div>
              </td>
            </tr>
          ) : (
            /* Data rows with staggered fade-in */
            <AnimatePresence initial={false}>
              {displayData.map((row: any, i: number) => {
                const rowKey = selectable
                  ? row[selectable.rowIdKey]
                  : `row-${i}`;
                const isSelected = selectable
                  ? selectable.selectedRows.some(
                      (r: any) =>
                        r[selectable.rowIdKey] === row[selectable.rowIdKey],
                    )
                  : false;

                return (
                  <motion.tr
                    key={rowKey}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.22,
                      delay: Math.min(i * 0.03, 0.25),
                    }}
                    className={twMerge(
                      "transition-colors border-b cursor-pointer h-20",
                      theme === "dark"
                        ? "border-gray-700 hover:bg-[#1f1f1f]"
                        : "border-gray-300 hover:bg-gray-100",
                      selectedRow?.id === row?.id
                        ? theme === "dark"
                          ? "bg-[#1a3a4a]"
                          : "bg-[#f5fbfd]"
                        : "",
                    )}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (
                        target.tagName === "INPUT" ||
                        target.tagName === "SELECT" ||
                        target.tagName === "BUTTON" ||
                        target.closest("button") ||
                        target.closest("select") ||
                        target.closest("input")
                      ) {
                        return;
                      }
                      setSelectedRow && setSelectedRow(row);
                    }}
                  >
                    {selectable?.active && (
                      <td className="px-2 pl-4 w-[40px]">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectable?.setSelectedRows((prev) => [
                                ...prev,
                                row,
                              ]);
                            } else {
                              selectable?.setSelectedRows((prev) =>
                                prev.filter(
                                  (r: any) =>
                                    r[selectable.rowIdKey] !==
                                    row[selectable.rowIdKey],
                                ),
                              );
                            }
                          }}
                        />
                      </td>
                    )}
                    {actions && (
                      <td
                        className={twMerge(
                          "px-4  border-b whitespace-nowrap",
                          theme === "dark"
                            ? "border-gray-700"
                            : "border-gray-300",
                        )}
                      >
                        {actions(row, i)}
                      </td>
                    )}
                    {columns.map((col, j) => (
                      <td
                        key={j}
                        className={twMerge(
                          "px-4 border-b whitespace-nowrap transition-colors",
                          theme === "dark"
                            ? "border-gray-700 text-gray-300"
                            : "border-gray-300 ",
                        )}
                      >
                        {col.render
                          ? col.render(row[col.accessor], row)
                          : (row[col.accessor] as any)}
                      </td>
                    ))}
                  </motion.tr>
                );
              })}
            </AnimatePresence>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
